import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { isSelectionValid } from '@miq/domain';
import moment from 'moment';
import { isNotNullOrEmptyString } from '../../../../../shared/utils/functions';
import { checkInstance, isDateSameOrAfter } from './details-modal.constants';
function validateMinMax(minControl: AbstractControl | null, maxControl: AbstractControl | null): boolean | null {
  if (!minControl || !maxControl) return null;

  const minValue = parseFloat(minControl.value?.replace(/[^\d.-]/g, '') || '');
  const maxValue = parseFloat(maxControl.value?.replace(/[^\d.-]/g, '') || '');

  if (isNaN(minValue) || isNaN(maxValue)) return null;

  if (minValue > maxValue) {
    minControl.setErrors({ invalidMinMax: true }, { emitEvent: true });
    return true;
  } else {
    minControl.setErrors(null, { emitEvent: true });
    maxControl.setErrors(null, { emitEvent: true });
  }

  return null;
}

export function validateMinMaxAskingRate(formGroup: UntypedFormGroup) {
  const controls = [
    {
      min: formGroup?.get('formMinMonthRentGroup')?.get('minAskingRateMo'),
      max: formGroup?.get('formMaxMonthRentGroup')?.get('maxAskingRateMo'),
    },
    {
      min: formGroup?.get('formMinAskingRateYrGroup')?.get('minAskingRateYr'),
      max: formGroup?.get('formMaxAskingRateYrGroup')?.get('maxAskingRateYr'),
    },
    {
      min: formGroup?.get('formGroupMinDivisible')?.get('minDivisibleSpace'),
      max: formGroup?.get('formGroupContiguousArea')?.get('contiguousArea'),
    },
  ];

  controls.forEach(({ min, max }) => validateMinMax(min, max));

  return null;
}

export function notEmptySpaceUOM(valueUomFG: UntypedFormGroup): { [key: string]: any } {
  const valueUOM = valueUomFG.value;
  return isNotNullOrEmptyString(valueUOM.spaceSize) && !isNotNullOrEmptyString(valueUOM.spaceSizeUOM)
    ? { emptyUOM: true }
    : null;
}
export function notEmptyRentUOM(valueUomFG: UntypedFormGroup): { [key: string]: any } {
  const valueUOM = valueUomFG.value;
  return isNotNullOrEmptyString(valueUOM.rentRate) && !isNotNullOrEmptyString(valueUOM.rentRateUOM)
    ? { emptyUOM: true }
    : null;
}

export function leaseDateValidator(leaseOrigin: 'start' | 'end'): ValidatorFn {
  return (leaseCtrl: UntypedFormControl) => {
    const siblingCtrlName = leaseOrigin === 'start' ? 'leaseEndDate' : 'leaseStartDate';
    const leaseSiblingCtrl = leaseCtrl.parent?.get(siblingCtrlName);
    const [start, end] =
      leaseOrigin === 'start' ? [leaseCtrl.value, leaseSiblingCtrl?.value] : [leaseSiblingCtrl?.value, leaseCtrl.value];
    if (isDateSameOrAfter(start, end)) {
      if (leaseSiblingCtrl?.invalid) {
        leaseSiblingCtrl?.setErrors(null);
        leaseSiblingCtrl?.updateValueAndValidity();
      }
      return null;
    }

    return { invalidDates: true };
  };
}

export function spaceSizeLessThanFloorSize(valueSpaceSizeForm: UntypedFormGroup): { invalidSpaceSize: boolean } {
  if (!checkInstance(this)) {
    return null;
  }
  const originalVal = this.space.getAreaSpace() || 0;
  const { floor_size } = this.containerRecordFloors.find(floor => floor.floor_number === this.space.floorNumber) || {};
  // * Disable validation if floor size is not defined...
  if (!floor_size) {
    return null;
  }

  const spaceSize = Number(valueSpaceSizeForm.value.spaceSize?.replace(/\D/g, ''));
  const isSpaceGreaterThanFloor = (this.unaccountedSpace || 0) + originalVal < spaceSize;
  valueSpaceSizeForm.get('spaceSize').markAsDirty();
  valueSpaceSizeForm['warnings'] = isSpaceGreaterThanFloor ? { invalidSpaceSize: true } : null;
  return null;
}

export function isAvailabilityVacant(valueUomFG: UntypedFormGroup): null {
  const dateAvailableCtrl = valueUomFG.parent?.get('dateAvailable');
  if (dateAvailableCtrl?.invalid || valueUomFG.value) {
    dateAvailableCtrl.updateValueAndValidity();
  }
  return null;
}

export function isDateAvailableValid(formGroup: UntypedFormGroup): { invalidVacancyAvailDate: boolean } {
  const vacantCtrl = formGroup?.get('vacant');
  const dateCtrl = formGroup?.get('dateAvailable');
  const possessionCtrl = formGroup?.get('possession');

  if (
    vacantCtrl?.value &&
    !(
      (dateCtrl.value != null && isDateSameOrAfter(dateCtrl.value, moment())) ||
      (possessionCtrl.value != null && possessionCtrl.value === 'Immediate')
    )
  ) {
    vacantCtrl.setErrors({ invalidVacancyAvailDate: true });
    dateCtrl.setErrors({ invalidVacancyAvailDate: true });
    possessionCtrl.setErrors({ invalidVacancyAvailDate: true });
    return { invalidVacancyAvailDate: true };
  }

  [vacantCtrl, dateCtrl, possessionCtrl].forEach(ctrl => {
    if (ctrl && ctrl.errors) {
      const errors = { ...ctrl.errors };
      delete errors['invalidVacancyAvailDate'];
      ctrl.setErrors(Object.keys(errors).length ? errors : null);
    }
  });

  return null;
}

export function isValidSpaceTenancyStatus(formCtrl: UntypedFormGroup): { invalidTenancyStatus: boolean } {
  const formGroup = formCtrl?.parent;
  const status = formGroup?.get('spaceLeaseStatus');
  const startDate = formGroup?.get('leaseStartDate');
  const endDate = formGroup?.get('leaseEndDate');
  const occupancyDate = formGroup?.get('spaceOccupancyDate');
  if (!isSelectionValid(status?.value, occupancyDate?.value, startDate?.value, endDate?.value)) {
    status.setErrors({ invalidTenancyStatus: true });
    return { invalidTenancyStatus: true };
  }
  status?.setErrors(null);
  return null;
}
